import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, PencilSimple, Trash, WhatsappLogo, Envelope, ChatCircleText, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';

const VARIABLES = [
  { key: 'tenant_name', label: 'اسم المستأجر' },
  { key: 'payment_no', label: 'رقم الدفعة' },
  { key: 'property_name', label: 'اسم العقار' },
  { key: 'branch', label: 'الفرع' },
  { key: 'due_date', label: 'تاريخ الاستحقاق' },
  { key: 'invoice_amount', label: 'مبلغ الفاتورة' },
  { key: 'collected_amount', label: 'المبلغ المحصل' },
  { key: 'remaining_amount', label: 'المبلغ المتبقي' },
  { key: 'contract_no', label: 'رقم العقد' },
  { key: 'phone', label: 'الجوال' },
];

const CHANNEL_META = {
  whatsapp: { icon: WhatsappLogo, label: 'واتساب', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  email: { icon: Envelope, label: 'بريد إلكتروني', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  sms: { icon: ChatCircleText, label: 'رسالة نصية', color: 'bg-amber-50 text-amber-700 border-amber-200' },
};

export default function MessageTemplates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: '', channel: 'whatsapp', subject: '', body: '' });

  const load = () => api.get('/message-templates').then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEdit(null); setForm({ name: '', channel: 'whatsapp', subject: '', body: '' }); setOpen(true); };
  const openEdit = (t) => { setEdit(t); setForm(t); setOpen(true); };

  const insertVar = (key) => {
    setForm(f => ({ ...f, body: (f.body || '') + `{{${key}}}` }));
  };

  const save = async () => {
    if (!form.name || !form.body) { toast.error('الاسم والنص مطلوبان'); return; }
    try {
      if (edit) await api.put(`/message-templates/${edit.id}`, form);
      else await api.post('/message-templates', form);
      toast.success(edit ? 'تم التحديث' : 'تم الإنشاء');
      setOpen(false); load();
    } catch (err) { toast.error('فشل: ' + (err.response?.data?.detail || err.message)); }
  };

  const del = async (id) => {
    if (!window.confirm('حذف القالب؟')) return;
    await api.delete(`/message-templates/${id}`);
    toast.success('تم الحذف'); load();
  };

  const copyBody = (body) => {
    navigator.clipboard.writeText(body);
    toast.success('تم النسخ');
  };

  return (
    <div className="space-y-5" data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#B4935A] mb-1">Message Templates</div>
          <h1 className="text-3xl font-bold text-slate-900">قوالب الرسائل</h1>
          <p className="text-sm text-slate-500 mt-1">قوالب جاهزة لواتساب والبريد الإلكتروني مع متغيرات ديناميكية</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 text-white" data-testid="add-template-button">
          <Plus className="ms-1" size={16} /> قالب جديد
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(t => {
          const meta = CHANNEL_META[t.channel] || CHANNEL_META.whatsapp;
          const Icon = meta.icon;
          return (
            <Card key={t.id} className="p-5 border border-slate-200 rounded-md shadow-none" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-md bg-slate-50 flex items-center justify-center">
                    <Icon size={18} className="text-slate-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900">{t.name}</div>
                    <Badge variant="outline" className={`${meta.color} border text-xs mt-1`}>{meta.label}</Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => copyBody(t.body)}><Copy size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(t)}><PencilSimple size={16} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(t.id)}><Trash size={16} className="text-red-600" /></Button>
                </div>
              </div>
              {t.subject && (
                <div className="mb-2 text-sm">
                  <span className="text-xs text-slate-500">العنوان:</span> <span className="text-slate-800 font-medium">{t.subject}</span>
                </div>
              )}
              <div className="text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-md p-3 whitespace-pre-wrap max-h-40 overflow-y-auto">
                {t.body}
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit ? 'تعديل قالب' : 'قالب جديد'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>اسم القالب</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="مثال: تذكير ودّي — واتساب" data-testid="template-name-input" />
            </div>
            <div>
              <Label>القناة</Label>
              <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} className="w-full h-10 rounded-md border border-slate-200 px-3 bg-white text-sm" data-testid="template-channel">
                <option value="whatsapp">واتساب</option>
                <option value="email">بريد إلكتروني</option>
                <option value="sms">رسالة نصية</option>
              </select>
            </div>
            {form.channel === 'email' && (
              <div>
                <Label>عنوان البريد</Label>
                <Input value={form.subject || ''} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="تذكير بفاتورة {{payment_no}}" />
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label>نص الرسالة</Label>
                <span className="text-xs text-slate-500">استخدم {'{{اسم_المتغير}}'} للحقول</span>
              </div>
              <Textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={8} data-testid="template-body-input" />
            </div>
            <div>
              <Label className="mb-2 block">إدراج متغير</Label>
              <div className="flex flex-wrap gap-1.5">
                {VARIABLES.map(v => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVar(v.key)}
                    className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-[#B4935A] hover:text-white text-slate-700 border border-slate-200 transition-colors"
                  >
                    <span className="mono">{`{{${v.key}}}`}</span>
                    <span className="mx-1 opacity-60">·</span>{v.label}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={save} className="w-full bg-slate-900 text-white" data-testid="save-template-button">حفظ</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
